import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from 'react';
import { I18n } from 'react-redux-i18n';
import { Modal } from 'semantic-ui-react';
import { Check, ChevronDown, Info, ExternalLink } from 'lucide-react';
import get from 'lodash.get';
import { getImagePath } from '../../helpers/imageUrlHelper';
import { getRegistrationUrl } from '../../helpers/registrationUrlHelper';

interface UrlOption {
  key: string;
  text: string;
  value: string;
  attribution: boolean;
}

interface CampaignLinksModalProps {
  open: boolean;
  entity: Record<string, unknown>;
  onClose: () => void;
}

interface UtmPreset {
  key: string;
  source: string;
  medium: string;
}

const I18N_PREFIX = 'campaign.performance.header.campaign-links-modal';

// i18nKey resolves under `${I18N_PREFIX}.utm-fields.*` at render time,
// once locale data is loaded
const UTM_FIELDS = [
  { key: 'utm_source', i18nKey: 'source', required: true },
  { key: 'utm_medium', i18nKey: 'medium', required: true },
  { key: 'utm_campaign', i18nKey: 'campaign', required: true },
  { key: 'utm_term', i18nKey: 'term', required: false },
  { key: 'utm_content', i18nKey: 'content', required: false }
] as const;

const REQUIRED_UTM_KEYS = UTM_FIELDS.filter((f) => f.required).map(
  (f) => f.key
);

// Labels resolve under `${I18N_PREFIX}.presets.*` at render time
const UTM_PRESETS: UtmPreset[] = [
  { key: 'email-newsletter', source: 'newsletter', medium: 'email' },
  { key: 'social-organic', source: 'facebook', medium: 'social' },
  { key: 'social-paid', source: 'facebook', medium: 'paid-social' },
  { key: 'print-qr', source: 'print', medium: 'qr-code' },
  { key: 'partner', source: 'partner', medium: 'referral' },
  { key: 'sms', source: 'sms', medium: 'text' }
];

function buildUrlOptions(entity: Record<string, unknown>): UrlOption[] {
  const urlFull = get(entity, 'urlFull', '') as string;
  if (!urlFull) return [];

  const options: UrlOption[] = [
    {
      key: 'campaign',
      text: I18n.t(`${I18N_PREFIX}.options.campaign-url`),
      value: urlFull,
      attribution: true
    },
    {
      key: 'donate',
      text: I18n.t(`${I18N_PREFIX}.options.donate`),
      value: `${urlFull}/donate`,
      attribution: true
    }
  ];

  const fundraisersEnabled = get(entity, 'fundraisers.enabled');
  const teamsEnabled = get(entity, 'teams.enabled');
  if (fundraisersEnabled || teamsEnabled) {
    options.push({
      key: 'join',
      text: I18n.t(`${I18N_PREFIX}.options.join`),
      value: `${urlFull}/join`,
      attribution: false
    });
  }

  if (get(entity, 'hasAmbassadorProgram')) {
    options.push({
      key: 'vip',
      text: I18n.t(`${I18N_PREFIX}.options.placehub`),
      value: `${urlFull}/vip/new`,
      attribution: true
    });
  }

  const registration = getRegistrationUrl(entity);
  if (registration) {
    options.push({
      key: 'ticket',
      text: I18n.t(`${I18N_PREFIX}.options.registration`),
      value: registration.url,
      attribution: registration.attribution
    });
  }

  return options;
}

function applyUtmParams(
  baseUrl: string,
  utmValues: Record<string, string>
): string {
  const entries = Object.entries(utmValues).filter(([, v]) => v.trim());
  if (entries.length === 0) return baseUrl;

  try {
    const parsed = new URL(baseUrl);
    entries.forEach(([k, v]) => parsed.searchParams.set(k, v.trim()));
    return parsed.toString();
  } catch {
    const hashIdx = baseUrl.indexOf('#');
    const withoutHash =
      hashIdx === -1 ? baseUrl : baseUrl.substring(0, hashIdx);
    const hash = hashIdx === -1 ? '' : baseUrl.substring(hashIdx);
    const qIdx = withoutHash.indexOf('?');
    const path = qIdx === -1 ? withoutHash : withoutHash.substring(0, qIdx);
    const params = new URLSearchParams(
      qIdx === -1 ? '' : withoutHash.substring(qIdx + 1)
    );
    entries.forEach(([k, v]) => params.set(k, v.trim()));
    return `${path}?${params.toString()}${hash}`;
  }
}

// UTF-8-safe base64: btoa alone throws on characters outside Latin-1
// (e.g. non-ASCII campaign names prefilled into utm_campaign).
function toBase64(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return window.btoa(binary);
}

function splitUrlAndUtm(
  fullUrl: string,
  baseUrl: string
): { base: string; utm: string } {
  if (!fullUrl || fullUrl === baseUrl) return { base: fullUrl, utm: '' };
  if (fullUrl.startsWith(baseUrl)) {
    return { base: baseUrl, utm: fullUrl.substring(baseUrl.length) };
  }
  const qIdx = fullUrl.indexOf('?');
  if (qIdx !== -1) {
    return { base: fullUrl.substring(0, qIdx), utm: fullUrl.substring(qIdx) };
  }
  return { base: fullUrl, utm: '' };
}

const O = '#FF5642';
const S = {
  body: { padding: '1.5rem' } as React.CSSProperties,
  twoCols: { display: 'flex', gap: '1.5rem' } as React.CSSProperties,
  left: { flex: 1, minWidth: 0 } as React.CSSProperties,
  right: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10
  } as React.CSSProperties,
  fieldLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text-muted, #888)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 8
  } as React.CSSProperties,
  pills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: '1.25rem'
  } as React.CSSProperties,
  pill: {
    fontSize: 12,
    padding: '6px 14px',
    borderRadius: 99,
    border: '1px solid var(--border-secondary, #ddd)',
    background: 'transparent',
    color: 'var(--text-primary, #333)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
    transition: 'all 0.15s'
  } as React.CSSProperties,
  pillActive: {
    fontSize: 12,
    padding: '6px 14px',
    borderRadius: 99,
    border: `1px solid ${O}`,
    background: O,
    color: '#fff',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
    transition: 'all 0.15s'
  } as React.CSSProperties,
  urlBlock: {
    background: 'var(--background-quaternary, #f5f5f5)',
    border: '1px solid var(--border-primary, #e8e8e8)',
    borderRadius: 8,
    padding: '10px 12px',
    marginBottom: 8
  } as React.CSSProperties,
  urlText: {
    fontSize: 13,
    fontFamily: "'SF Mono', 'Fira Code', Consolas, monospace",
    color: 'var(--text-primary, #333)',
    wordBreak: 'break-all',
    lineHeight: 1.6
  } as React.CSSProperties,
  urlUtm: { color: 'var(--text-muted, #999)' } as React.CSSProperties,
  urlActions: { display: 'flex', gap: 8 } as React.CSSProperties,
  btnCopy: {
    whiteSpace: 'nowrap',
    background: O,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
    minWidth: 86,
    transition: 'background 0.15s'
  } as React.CSSProperties,
  btnOpen: {
    background: 'var(--background-primary, #fff)',
    color: 'var(--text-secondary, #555)',
    border: '1px solid var(--border-secondary, #ddd)',
    borderRadius: 8,
    padding: '8px 14px',
    fontSize: 13,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s',
    display: 'flex',
    alignItems: 'center',
    gap: 5
  } as React.CSSProperties,
  attributionHint: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 6,
    marginTop: 10,
    padding: '8px 10px',
    background: 'var(--gr-light-blue, #f0f7ff)',
    borderRadius: 6,
    fontSize: 12,
    color: 'var(--text-secondary, #4a7ab5)',
    lineHeight: 1.4
  } as React.CSSProperties,
  attributionIcon: {
    flexShrink: 0,
    marginTop: 1,
    color: 'var(--gr-blue, #4a7ab5)'
  } as React.CSSProperties,
  accordion: {
    border: '1px solid var(--border-primary, #e8e8e8)',
    borderRadius: 8,
    marginTop: '1.25rem'
  } as React.CSSProperties,
  accordionToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    cursor: 'pointer',
    userSelect: 'none',
    background: 'none',
    border: 'none',
    width: '100%',
    fontFamily: 'inherit',
    borderRadius: 8
  } as React.CSSProperties,
  accordionToggleLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  } as React.CSSProperties,
  utmCheck: {
    width: 16,
    height: 16,
    borderRadius: 4,
    border: '1.5px solid var(--border-secondary, #ccc)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    transition: 'all 0.15s',
    flexShrink: 0
  } as React.CSSProperties,
  utmCheckActive: {
    width: 16,
    height: 16,
    borderRadius: 4,
    border: `1.5px solid ${O}`,
    background: O,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    transition: 'all 0.15s',
    flexShrink: 0
  } as React.CSSProperties,
  utmFields: {
    padding: '0 12px 12px',
    borderTop: '1px solid var(--border-primary, #e8e8e8)'
  } as React.CSSProperties,
  presetsLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text-muted, #888)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 6,
    paddingTop: 10
  } as React.CSSProperties,
  presetsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: 6,
    marginBottom: 12
  } as React.CSSProperties,
  presetBtn: {
    fontSize: 11,
    padding: '6px 8px',
    border: '1px solid var(--border-primary, #e8e8e8)',
    borderRadius: 6,
    background: 'var(--background-secondary, #fafafa)',
    color: 'var(--text-secondary, #555)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s',
    textAlign: 'center',
    whiteSpace: 'nowrap'
  } as React.CSSProperties,
  presetBtnActive: {
    fontSize: 11,
    padding: '6px 8px',
    border: `1px solid ${O}`,
    borderRadius: 6,
    background: O,
    color: '#fff',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s',
    textAlign: 'center',
    whiteSpace: 'nowrap'
  } as React.CSSProperties,
  utmGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 8
  } as React.CSSProperties,
  utmFieldLabel: {
    fontSize: 11,
    color: 'var(--text-muted, #888)',
    display: 'block',
    marginBottom: 3
  } as React.CSSProperties,
  utmInput: {
    width: '100%',
    border: '1px solid var(--border-secondary, #ddd)',
    borderRadius: 6,
    padding: '7px 8px',
    fontSize: 13,
    fontFamily: 'inherit',
    color: 'var(--text-primary, #333)',
    transition: 'border-color 0.15s'
  } as React.CSSProperties,
  qrContainer: {
    width: 164,
    height: 164,
    background: 'var(--background-quaternary, #f5f5f5)',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid var(--border-primary, #e8e8e8)',
    overflow: 'hidden'
  } as React.CSSProperties,
  qrLogoToggle: {
    fontSize: 12,
    color: 'var(--text-secondary, #666)',
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    cursor: 'pointer'
  } as React.CSSProperties,
  qrCheckbox: { width: 14, height: 14, accentColor: O } as React.CSSProperties,
  btnDownload: {
    fontSize: 12,
    padding: '7px 14px',
    width: 164,
    background: 'var(--background-primary, #fff)',
    border: '1px solid var(--border-secondary, #ddd)',
    borderRadius: 8,
    cursor: 'pointer',
    fontFamily: 'inherit',
    color: 'var(--text-primary, #333)',
    transition: 'all 0.15s',
    textAlign: 'center'
  } as React.CSSProperties
};

export const CampaignLinksModal: React.FC<CampaignLinksModalProps> = ({
  open,
  entity,
  onClose
}) => {
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCopiedTimer = useCallback(() => {
    if (copiedTimerRef.current) {
      clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = null;
    }
  }, []);

  useEffect(() => clearCopiedTimer, [clearCopiedTimer]);
  const [showUtm, setShowUtm] = useState(false);
  const [utmValues, setUtmValues] = useState<Record<string, string>>({});
  const [branded, setBranded] = useState(true);
  const [activePreset, setActivePreset] = useState<string | null>(null);

  const urlOptions = useMemo(() => buildUrlOptions(entity), [entity]);
  const [selectedKey, setSelectedKey] = useState('campaign');

  const selectedOption = useMemo(
    () => urlOptions.find((o) => o.key === selectedKey) || urlOptions[0],
    [urlOptions, selectedKey]
  );
  const selectedBaseUrl = selectedOption?.value || '';

  const utmComplete = useMemo(
    () => REQUIRED_UTM_KEYS.every((k) => (utmValues[k] || '').trim()),
    [utmValues]
  );

  const finalUrl = useMemo(
    () =>
      showUtm && utmComplete
        ? applyUtmParams(selectedBaseUrl, utmValues)
        : selectedBaseUrl,
    [selectedBaseUrl, showUtm, utmComplete, utmValues]
  );

  const urlParts = useMemo(
    () => splitUrlAndUtm(finalUrl, selectedBaseUrl),
    [finalUrl, selectedBaseUrl]
  );

  const orgLogo = get(entity, 'organization.mainImagePath', '') as string;
  const campaignName = get(entity, 'name', '') as string;

  const qrCodeUrl = useMemo(() => {
    if (!finalUrl) return '';
    const base64Code = encodeURIComponent(toBase64(finalUrl));
    const logoParam =
      branded && orgLogo ? `&logo=${encodeURIComponent(orgLogo)}` : '';
    return getImagePath(`qr?b=${base64Code}${logoParam}`);
  }, [finalUrl, branded, orgLogo]);

  const handleCopy = useCallback(async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(finalUrl);
      } else {
        const ta = document.createElement('textarea');
        ta.value = finalUrl;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      clearCopiedTimer();
      copiedTimerRef.current = setTimeout(() => setCopied(false), 2500);
    } catch {
      // silently fail
    }
  }, [finalUrl, clearCopiedTimer]);

  const handleVisit = useCallback(() => {
    window.open(finalUrl, '_blank', 'noopener,noreferrer');
  }, [finalUrl]);

  const handleClose = useCallback(() => {
    clearCopiedTimer();
    setCopied(false);
    setShowUtm(false);
    setActivePreset(null);
    setUtmValues({});
    setSelectedKey('campaign');
    setBranded(true);
    onClose();
  }, [onClose, clearCopiedTimer]);

  const handleUtmChange = useCallback((key: string, value: string) => {
    setUtmValues((prev) => ({ ...prev, [key]: value }));
    setActivePreset(null);
  }, []);

  const toggleUtm = useCallback(() => {
    setShowUtm((prev) => !prev);
  }, []);

  const applyPreset = useCallback(
    (preset: UtmPreset) => {
      if (activePreset === preset.key) {
        setUtmValues({});
        setActivePreset(null);
      } else {
        setUtmValues({
          utm_source: preset.source,
          utm_medium: preset.medium,
          utm_campaign: campaignName,
          utm_term: '',
          utm_content: ''
        });
        setActivePreset(preset.key);
      }
    },
    [activePreset, campaignName]
  );

  if (urlOptions.length === 0) return null;

  const showAttribution = showUtm && utmComplete && selectedOption?.attribution;

  return (
    <Modal
      size="small"
      dimmer="inverted"
      closeIcon
      open={open}
      onClose={handleClose}
      style={{ borderRadius: 12, overflow: 'hidden', maxWidth: 720 }}
    >
      <Modal.Header
        style={{
          padding: '1.25rem 1.5rem',
          borderBottom: '1px solid var(--border-primary, #e8e8e8)',
          fontSize: 16,
          fontWeight: 600
        }}
      >
        {I18n.t(`${I18N_PREFIX}.title`)}
      </Modal.Header>
      <Modal.Content style={{ padding: 0 }}>
        <div style={S.body}>
          <div style={S.twoCols}>
            {/* Left column */}
            <div style={S.left}>
              <div style={S.fieldLabel}>
                {I18n.t(`${I18N_PREFIX}.url-label`)}
              </div>
              <div style={S.pills}>
                {urlOptions.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    aria-pressed={selectedKey === opt.key}
                    style={selectedKey === opt.key ? S.pillActive : S.pill}
                    onClick={() => setSelectedKey(opt.key)}
                  >
                    {opt.text}
                  </button>
                ))}
              </div>

              <div style={S.fieldLabel}>
                {I18n.t(`${I18N_PREFIX}.generated-url`)}
              </div>
              <div style={S.urlBlock}>
                <div style={S.urlText}>
                  <span>{urlParts.base}</span>
                  {urlParts.utm && <span style={S.urlUtm}>{urlParts.utm}</span>}
                </div>
              </div>
              <div style={S.urlActions}>
                <button type="button" style={S.btnCopy} onClick={handleCopy}>
                  {copied
                    ? I18n.t('general.copied')
                    : I18n.t('general.copy-link')}
                </button>
                <button type="button" style={S.btnOpen} onClick={handleVisit}>
                  <ExternalLink size={13} aria-hidden="true" />
                  {I18n.t(`${I18N_PREFIX}.visit`)}
                </button>
              </div>

              {showAttribution && (
                <div style={S.attributionHint}>
                  <Info
                    size={14}
                    style={S.attributionIcon}
                    aria-hidden="true"
                  />
                  <span>
                    {I18n.t(`${I18N_PREFIX}.attribution.${selectedOption.key}`)}
                  </span>
                </div>
              )}
            </div>

            {/* Right column: QR */}
            <div style={S.right}>
              <div style={S.qrContainer}>
                {qrCodeUrl && (
                  <img
                    src={qrCodeUrl}
                    alt={I18n.t(`${I18N_PREFIX}.qr-alt`)}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />
                )}
              </div>
              {orgLogo && (
                <label style={S.qrLogoToggle}>
                  <input
                    type="checkbox"
                    checked={branded}
                    onChange={(e) => setBranded(e.target.checked)}
                    style={S.qrCheckbox}
                  />
                  {I18n.t(`${I18N_PREFIX}.qr-branded`)}
                </label>
              )}
              {qrCodeUrl && (
                <a
                  href={qrCodeUrl}
                  download="qr-code.png"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    ...S.btnDownload,
                    textDecoration: 'none',
                    display: 'block'
                  }}
                >
                  {I18n.t(`${I18N_PREFIX}.qr-download`)}
                </a>
              )}
            </div>
          </div>

          {/* UTM Accordion: full-width below two columns */}
          <div style={S.accordion}>
            <button
              type="button"
              aria-expanded={showUtm}
              style={S.accordionToggle}
              onClick={toggleUtm}
            >
              <div style={S.accordionToggleLeft}>
                <div style={showUtm ? S.utmCheckActive : S.utmCheck}>
                  {showUtm && <Check size={10} aria-hidden="true" />}
                </div>
                <span
                  style={{ fontSize: 13, color: 'var(--text-primary, #333)' }}
                >
                  {I18n.t(`${I18N_PREFIX}.utm-toggle`)}
                </span>
              </div>
              <ChevronDown
                size={14}
                aria-hidden="true"
                style={{
                  color: 'var(--text-muted, #999)',
                  transition: 'transform 0.2s',
                  transform: showUtm ? 'rotate(180deg)' : 'rotate(0deg)'
                }}
              />
            </button>
            {showUtm && (
              <div style={S.utmFields}>
                {/* Quick-fill presets */}
                <div style={S.presetsLabel}>
                  {I18n.t(`${I18N_PREFIX}.presets-label`)}
                </div>
                <div style={S.presetsGrid}>
                  {UTM_PRESETS.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      aria-pressed={activePreset === p.key}
                      style={
                        activePreset === p.key ? S.presetBtnActive : S.presetBtn
                      }
                      onClick={() => applyPreset(p)}
                    >
                      {I18n.t(`${I18N_PREFIX}.presets.${p.key}`)}
                    </button>
                  ))}
                </div>

                {!utmComplete && (
                  <div
                    style={{
                      fontSize: 12,
                      color: O,
                      padding: '0 0 8px',
                      lineHeight: 1.4
                    }}
                  >
                    {I18n.t(`${I18N_PREFIX}.utm-incomplete`)}
                  </div>
                )}
                <div style={S.utmGrid}>
                  {UTM_FIELDS.slice(0, 4).map((f) => (
                    <div key={f.key}>
                      <label
                        htmlFor={`campaign-links-${f.key}`}
                        style={S.utmFieldLabel}
                      >
                        {I18n.t(`${I18N_PREFIX}.utm-fields.${f.i18nKey}.label`)}
                        {f.required && (
                          <span style={{ color: O, marginLeft: 2 }}>*</span>
                        )}
                        {!f.required && (
                          <span
                            style={{
                              color: 'var(--text-muted, #aaa)',
                              marginLeft: 4,
                              fontWeight: 400,
                              fontStyle: 'italic'
                            }}
                          >
                            {I18n.t(`${I18N_PREFIX}.utm-optional`)}
                          </span>
                        )}
                      </label>
                      <input
                        id={`campaign-links-${f.key}`}
                        type="text"
                        placeholder={I18n.t(
                          `${I18N_PREFIX}.utm-fields.${f.i18nKey}.placeholder`
                        )}
                        value={utmValues[f.key] || ''}
                        onChange={(e) => handleUtmChange(f.key, e.target.value)}
                        style={{
                          ...S.utmInput,
                          ...(f.required && !(utmValues[f.key] || '').trim()
                            ? { borderColor: O }
                            : {})
                        }}
                      />
                    </div>
                  ))}
                </div>
                <div
                  style={{
                    ...S.utmGrid,
                    gridTemplateColumns: '1fr',
                    marginTop: 8
                  }}
                >
                  <div>
                    <label
                      htmlFor={`campaign-links-${UTM_FIELDS[4].key}`}
                      style={S.utmFieldLabel}
                    >
                      {I18n.t(
                        `${I18N_PREFIX}.utm-fields.${UTM_FIELDS[4].i18nKey}.label`
                      )}
                      <span
                        style={{
                          color: 'var(--text-muted, #aaa)',
                          marginLeft: 4,
                          fontWeight: 400,
                          fontStyle: 'italic'
                        }}
                      >
                        {I18n.t(`${I18N_PREFIX}.utm-optional`)}
                      </span>
                    </label>
                    <input
                      id={`campaign-links-${UTM_FIELDS[4].key}`}
                      type="text"
                      placeholder={I18n.t(
                        `${I18N_PREFIX}.utm-fields.${UTM_FIELDS[4].i18nKey}.placeholder`
                      )}
                      value={utmValues[UTM_FIELDS[4].key] || ''}
                      onChange={(e) =>
                        handleUtmChange(UTM_FIELDS[4].key, e.target.value)
                      }
                      style={S.utmInput}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal.Content>
    </Modal>
  );
};
